import { t } from '@lingui/core/macro';
import { i18n } from '@lingui/core';

/**
 * Get the active app locale for Intl APIs.
 */
function getAppLocale(): string {
  return i18n.locale || 'en';
}

/**
 * Format a timestamp to a locale-aware relative time string using Intl.RelativeTimeFormat.
 * Uses the narrow style for compact display (e.g., "3d ago", "2w ago").
 */
export function formatRelativeTime(timestamp: number): string {
  const now = Date.now();
  const diff = now - timestamp * 1000; // Convert Unix timestamp to milliseconds

  const seconds = Math.floor(diff / 1000);
  const minutes = Math.floor(seconds / 60);
  const hours = Math.floor(minutes / 60);
  const days = Math.floor(hours / 24);
  const weeks = Math.floor(days / 7);
  const months = Math.floor(days / 30);
  const years = Math.floor(days / 365);

  if (seconds < 60) {
    return t`Just now`;
  }

  const rtf = new Intl.RelativeTimeFormat(getAppLocale(), {
    numeric: 'always',
    style: 'narrow',
  });

  if (years > 0) return rtf.format(-years, 'year');
  if (months > 0) return rtf.format(-months, 'month');
  if (weeks > 0) return rtf.format(-weeks, 'week');
  if (days > 0) return rtf.format(-days, 'day');
  if (hours > 0) return rtf.format(-hours, 'hour');
  return rtf.format(-minutes, 'minute');
}

/**
 * Format a Date using Intl.DateTimeFormat with the app's active locale.
 */
export function formatDate(
  date: Date | number | string,
  options?: Intl.DateTimeFormatOptions,
): string {
  const dateObj =
    date instanceof Date
      ? date
      : new Date(typeof date === 'number' ? date : Date.parse(date));

  return new Intl.DateTimeFormat(getAppLocale(), options).format(dateObj);
}
